import { AIChatbotEvents, trackAIChatbotEvent } from 'Analytics/ai-chatbot-events';
import { VuMediChatbotInline } from 'Modules/chatbot';
import 'Modules/chatbot/components/vm-chat-inline/vm-chat-inline';
import 'Modules/chatbot/components/vm-chat-mobile-trigger/vm-chat-mobile-trigger';
import { UserStore } from 'Modules/main/user-store';
import { getInterstitialStates, setDynamicState } from 'Utils/dynamic-state';
import { VaiaProductTour } from 'Utils/product-tours';
import { isDesktop, isLandscape } from 'Utils/viewport';

const context = window.__VUMEDI_VIDEO_DETAIL_CONTEXT__;

/**
 * Show AI assistant tooltip, product tour.
 *
 * If forced = true, a smaller version of the tooltip is displayed. This smaller version is
 * used to display AI features to users that have already seen the whole product tour.
 */

const showProductTour = (forced = false, dynamicQuestionsReady = Promise.resolve()) => {
    // Do not show the tour if in mobile landscape mode
    if (!isDesktop() && isLandscape()) {
        return;
    }

    const tour = VaiaProductTour(forced);

    // Wait for dynamic questions to finish loading before starting the tour.
    // This prevents the tour tooltip from jumping to (0, 0) when the DOM
    // updates underneath the anchored tooltip element.
    dynamicQuestionsReady.finally(() => {
        const handleTourDone = () => {
            setDynamicState('chatbot-tooltip', false);
            // If the tour was canceled due to landscape mode, do not mark as seen
            if (tour.landscapeCancelled) {
                return;
            }

            trackAIChatbotEvent(AIChatbotEvents.TOOLTIP_CLICKED);
            UserStore.saveVAIATooltipSeen(true);
        };

        tour.on('cancel', handleTourDone);
        tour.on('complete', handleTourDone);

        setDynamicState('chatbot-tooltip', true);
        tour.start();
    });
};

export function setupChatbot(brightcovePlayer) {
    return new Promise((resolve) => {
        const container = document.querySelector('.js_vmChatbotInline');
        const mobileTriggerContainer = document.querySelector('.js_vmChatbotInlineMobileTrigger');

        // Chat configuration requires a logged-in user
        if (!context.isAuthenticated || !container || !mobileTriggerContainer) {
            setDynamicState('chatbot-tooltip', false);
            resolve(false);
            return;
        }

        // `null` if decision is not resolved, true|false otherwise
        const shouldBlockTooltip = () => {
            const states = getInterstitialStates();

            if (states.findIndex((state) => typeof state.data === 'undefined') > -1) {
                return null;
            }

            // If at least one `state.data` is `true`, tooltip should be blocked because
            // interstitial is displayed
            return states.reduce((acc, state) => acc || state.data, false);
        };

        const finalize = (initialized, shouldBlockTooltip) => {
            // Don't show the tooltip if an interstitial should be displayed
            if (shouldBlockTooltip) {
                setDynamicState('chatbot-tooltip', false);
                resolve(false);
                return;
            }

            // The final check before showing the tooltip is done by checking
            // if the server also said to display the product tour (tooltip)
            if (initialized && chatbot.showProductTour) {
                // If a tooltip has been seen by the user, force the minimal version
                showProductTour(tooltipSeen, chatbot.dynamicQuestionsReady);
                resolve(true);
                return;
            }

            setDynamicState('chatbot-tooltip', false);
            resolve(false);
        };

        // Determine whether the client app wants to show the tooltip
        const tooltipSeen = UserStore.loadVAIATooltipSeen();
        const tooltipForced = new URL(window.location.href).searchParams.get('ai-summary') !== null;
        const showTooltip = !tooltipSeen || tooltipForced;

        const chatbot = new VuMediChatbotInline(
            parseInt(context.videoId, 10),
            context.viewedUUID,
            brightcovePlayer,
            context.videoSlug,
            showTooltip,
        );

        chatbot
            .initialize(container, mobileTriggerContainer)
            .then((initialized) => {
                const handleDynamicStateChange = () => {
                    const shouldBlock = shouldBlockTooltip();

                    if (shouldBlock === false) {
                        window.removeEventListener('dynamicstatechange', handleDynamicStateChange);
                        // Show the tooltip as a side effect, but resolve with false so
                        // the brand alert is not killed via 'chatbot_active_dismiss'.
                        if (initialized && chatbot.showProductTour) {
                            showProductTour(tooltipSeen, chatbot.dynamicQuestionsReady);
                        } else {
                            setDynamicState('chatbot-tooltip', false);
                        }
                        resolve(false);
                    } else if (shouldBlock === true) {
                        // Interstitial active — unblock brand alerts now; keep listener so
                        // the tooltip can show once the interstitial is dismissed.
                        resolve(false);
                    }
                    // null: states still loading — keep waiting
                };

                const shouldBlock = shouldBlockTooltip();

                if (shouldBlock === false) {
                    finalize(initialized, false);
                } else {
                    window.addEventListener('dynamicstatechange', handleDynamicStateChange);
                    if (shouldBlock === true) {
                        // Interstitial already showing — resolve immediately so brand alerts start.
                        resolve(false);
                    }
                    // null: states still loading — leave promise pending until state resolves
                }
            })
            .catch(() => {
                setDynamicState('chatbot-tooltip', false);
                resolve(false);
            });
    });
}
